import { useEffect, useState } from 'react';
import { contentService } from '../services';
import { useOrganization } from '../context/OrganizationContext';

/**
 * Module-level cache of in-flight and resolved thumbnail object URLs, keyed
 * by `${teamId}:${contentId}`. Lives for the SPA's lifetime — cleared on
 * page reload. Storing the Promise (not the URL) makes concurrent mounts
 * for the same content dedupe to a single network request, and remounts
 * after navigation reuse the same blob URL with no traffic at all.
 *
 * Object URLs are intentionally never revoked: they're owned by the cache,
 * not by the components that read them. The trade is ~30KB per unique
 * thumbnail viewed in exchange for instant re-renders and a quieter
 * gateway. At typical browsing volumes this is well within budget; if
 * memory ever matters more than latency, this Map is the place to add LRU.
 */
const thumbnailCache = new Map<string, Promise<string>>();

const cacheKey = (teamId: string, contentId: string) => `${teamId}:${contentId}`;

const fetchThumbnailUrl = (teamId: string, contentId: string): Promise<string> => {
  const key = cacheKey(teamId, contentId);
  const existing = thumbnailCache.get(key);
  if (existing) return existing;

  const promise = contentService
    .downloadContentThumbnail(contentId, teamId)
    .then((blob) => URL.createObjectURL(blob))
    .catch((error) => {
      // Don't poison the cache with failures — let the next attempt retry.
      thumbnailCache.delete(key);
      throw error;
    });

  thumbnailCache.set(key, promise);
  return promise;
};

/**
 * Fetches a content thumbnail via the authenticated content service and
 * returns an object URL suitable for `<img src>`. Returns `undefined` when
 * the content has no thumbnail or the fetch is still in flight, so callers
 * naturally fall back to the file-type illustration.
 *
 * The `hint` argument is the server-emitted `thumbnailUrl` field (the
 * secured gateway URL). Its value isn't dereferenced — it only acts as
 * a "thumbnail exists for this content" signal. An empty/undefined hint
 * skips the fetch entirely.
 *
 * Results are cached at module scope, so the same content's thumbnail is
 * fetched at most once per session regardless of how many tiles, rows, or
 * navigations reference it.
 */
/**
 * Detects a fully-qualified URL that should be used as-is rather than dereferenced
 * through the authenticated content service. The public-link resolve endpoint emits
 * presigned bucket URLs (e.g. R2/S3) that anonymous browsers can fetch directly; the
 * auth'd path is meaningless in those contexts because we don't have a session token.
 */
const isDirectFetchUrl = (hint: string | undefined): boolean => {
  if (!hint) return false;
  return /^https?:\/\//i.test(hint) && !/^https?:\/\/[^/]+\/api\//i.test(hint);
};

export function useContentThumbnail(
  contentId: string | undefined,
  hint: string | undefined,
): string | undefined {
  const { currentTeamId } = useOrganization();
  const [url, setUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!contentId || !hint) {
      setUrl(undefined);
      return;
    }

    // Anonymous / presigned URLs (e.g. from publicLinksService.resolveComponent):
    // the hint IS the fetchable URL. Use it directly without an authenticated
    // call to the content service.
    if (isDirectFetchUrl(hint)) {
      setUrl(hint);
      return;
    }

    if (!currentTeamId) {
      setUrl(undefined);
      return;
    }

    let cancelled = false;

    fetchThumbnailUrl(currentTeamId, contentId)
      .then((objectUrl) => {
        if (!cancelled) setUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setUrl(undefined);
      });

    return () => {
      cancelled = true;
      // Do NOT revoke objectUrl — it's cache-owned and shared across
      // every mount that references this content.
    };
  }, [contentId, hint, currentTeamId]);

  return url;
}
